import React from 'react';
import {Map} from 'immutable';
import {buttonStyle} from './styles';
import {Plugins, withSystem, Atom} from '@packages/systems/wfdl';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {px} from '@packages/systems/core/utils/WebflowCssSubset';
import {createRgbaColor} from '@packages/systems/core/utils/ColorUtils';
import {
  DATA_ATTR_NODE_TYPE,
  NODE_TYPE_COMMERCE_CART_APPLE_PAY_BUTTON,
  QUICK_CHECKOUT_STATES,
  EDITABLE_STYLE_NAMES,
} from '@packages/systems/commerce/constants';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {TListOfElement, TRecord, TText} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {useRuntimeEnvironment} from '@packages/systems/render';
import {getChildren} from '@packages/utilities/data-utils';

const {SSR, EXPORT} = WebflowEnvironments;
const styleDisplayNone = Map({display: 'none'});
// we have apple pay styles on the button for the published site, since we set it as a background image
// this makes sure that if the button is resized, the background will scale properly with it
const styleApplePayBackground = Map({
  backgroundImage: '-webkit-named-image(apple-pay-logo-white)',
  backgroundSize: '100% 50%',
  backgroundPosition: '50% 50%',
  backgroundRepeat: 'no-repeat',
});

const CommerceCartApplePayButton = Atom.createRender((props) => {
  const {attributes, classes, context, data, generateAttributes, renderChild} =
    props;

  const runtimeEnvironment = useRuntimeEnvironment();
  let styleMap = Map();
  if (context.get('previewState') !== QUICK_CHECKOUT_STATES.APPLE_PAY) {
    styleMap = styleMap.merge(styleDisplayNone);
  }
  if (runtimeEnvironment === SSR || runtimeEnvironment === EXPORT) {
    styleMap = styleMap.merge(styleApplePayBackground);
  }

  const styledAttributes = !styleMap.isEmpty()
    ? // @ts-expect-error - TS2769 - No overload matches this call.
      attributes.merge(Map({style: styleMap}))
    : attributes;
  return (
    // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'number'.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    <a {...generateAttributes(styledAttributes, classes)} tabIndex="0">
      {getChildren(data).map((child) => renderChild(child, context))}
    </a>
  );
});

const lockedStyles = (pseudoMode: any) => {
  const allowedStyleNames = ['borderRadius', 'height', 'width'];

  if (['focus', 'focus-visible'].includes(pseudoMode)) {
    allowedStyleNames.push(
      'outlineColor',
      'outlineOffset',
      'outlineStyle',
      'outlineWidth'
    );
  }

  return EDITABLE_STYLE_NAMES.reduce<Record<string, any>>((obj, styleName) => {
    if (!allowedStyleNames.includes(styleName)) {
      obj[styleName] = {
        when: true,
        message:
          "You can only edit the width, height and border radius of an Apple Pay Button, due to Apple's Human Interface Guidelines. You can edit outline styles when styling the Focused or Focused (keyboard) states.",
      };
    }
    return obj;
  }, {});
};

const config = createIntegratedAtom({
  displayName: 'Apple Pay',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Basic'), iconName('Button')),
  },
  showEmpty: false,
  style: {
    base: {
      main: {
        noPseudo: {
          ...buttonStyle,
          display: 'flex',
          alignItems: 'center',
          paddingTop: px(0),
          paddingBottom: px(0),
          paddingLeft: px(0),
          paddingRight: px(0),
          borderTopLeftRadius: px(2),
          borderTopRightRadius: px(2),
          borderBottomRightRadius: px(2),
          borderBottomLeftRadius: px(2),
          marginBottom: px(8),
          height: px(38),
          minHeight: px(30),
          backgroundColor: createRgbaColor('000000'),
        },
      },
    },
  },
  constraints: {
    ancestors: [{is: 'CommerceCartQuickCheckoutActions', rule: 'ExactlyOne'}],
    descendants: [
      {is: 'CommerceCartApplePayIcon', rule: 'ExactlyOne'},
      {
        is: 'CommerceCartApplePayIcon',
        name: 'Element',
        rule: 'RequireOnly',
      },
    ],
  },
  // this errors flow due to `border` and `textColumns` not being part of `WebflowCssProperties`,
  // but we need to set those for the styles panel to disable the sections
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  lockedStyles,
  render: CommerceCartApplePayButton,
  fields: {
    children: TListOfElement,
    attr: withSystem(
      {private: false},
      TRecord({
        role: withSystem({private: true, defaultValue: 'button'}, TText),
        'aria-haspopup': withSystem(
          {private: true, defaultValue: 'dialog'},
          TText
        ),
        'aria-label': withSystem(
          {private: true, defaultValue: 'Apple Pay'},
          TText
        ),
        [DATA_ATTR_NODE_TYPE]: withSystem(
          {
            private: true,
            defaultValue: NODE_TYPE_COMMERCE_CART_APPLE_PAY_BUTTON,
          },
          TText
        ),
      })
    ),
  },
});

export default config;
